import React from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { logger } from "../helpers/loggerHelper";
import { ENV } from "../config/env";

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 40px 20px;
  background: linear-gradient(
    135deg,
    color-mix(in srgb, var(--neutral-white) 90%, transparent) 0%,
    color-mix(in srgb, var(--neutral-white) 95%, transparent) 100%
  );
  text-align: center;
`;

const ErrorCard = styled.div`
  max-width: 500px;
  padding: 48px;
  background: color-mix(in srgb, var(--neutral-white) 90%, transparent);
  border-radius: var(--radius-container);
  border: 1px solid var(--neutral-200);
  box-shadow: none;
  backdrop-filter: blur(8px);
`;

const ErrorIcon = styled.div`
  font-size: 64px;
  margin-bottom: 24px;
  opacity: 0.8;
`;

const ErrorTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: var(--palette-text-primary);
  margin: 0 0 16px 0;
`;

const ErrorMessage = styled.p`
  font-size: 16px;
  color: var(--palette-text-primary);
  margin: 0 0 32px 0;
  line-height: 1.6;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: var(--radius-control);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid var(--neutral-200);

  &.primary {
    background: var(--primary-base);
    color: var(--palette-primary-contrastText);

    &:hover {
      background: var(--primary-base-hover);
      transform: translateY(-2px);
      box-shadow: none;
    }
  }

  &.secondary {
    background: var(--neutral-200);
    color: var(--neutral-700);

    &:hover {
      background: var(--neutral-200);
    }
  }

  &:active {
    transform: translateY(0);
  }
`;

const ErrorDetails = styled.details`
  margin-top: 24px;
  padding: 16px;
  background: var(--neutral-200);
  border-radius: var(--radius-container);
  text-align: left;
  font-size: 12px;
  color: var(--neutral-600);
  font-family: monospace;

  summary {
    cursor: pointer;
    font-weight: 600;
    margin-bottom: 8px;
    user-select: none;

    &:hover {
      color: var(--primary-base);
    }
  }

  pre {
    margin: 8px 0 0 0;
    overflow-x: auto;
    white-space: pre-wrap;
    word-wrap: break-word;
  }
`;

/**
 * RouteErrorBoundary Component
 *
 * A specialized error boundary for handling route-level errors.
 * Provides a user-friendly error UI with recovery options.
 *
 * Features:
 * - Beautiful glassmorphism design matching app theme
 * - Multiple recovery options (retry, home, back)
 * - Collapsible error details for developers
 * - Automatic error logging
 *
 * @component
 * @example
 * <RouteErrorBoundary>
 *   <YourRoute />
 * </RouteErrorBoundary>
 */
type RouteErrorBoundaryProps = {
  children?: React.ReactNode;
};

type RouteErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  errorId: string | null;
};

type RouteErrorFallbackProps = {
  error?: Error | null;
  resetErrorBoundary: () => void;
};

class RouteErrorBoundary extends React.Component<
  RouteErrorBoundaryProps,
  RouteErrorBoundaryState
> {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    };
  }

  static getDerivedStateFromError(_error) {
    // Generate unique error ID for tracking
    const errorId = `route_err_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;

    return {
      hasError: true,
      errorId,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error for monitoring
    try {
      logger.error("RouteErrorBoundary caught an error", {
        errorId: this.state.errorId,
        error: error.toString(),
        errorInfo,
        componentStack: errorInfo.componentStack,
      });
    } catch (logError) {
      // Fallback if logger fails
      // eslint-disable-next-line no-console
      console.error("RouteErrorBoundary error:", {
        error,
        errorInfo,
        logError,
      });
    }

    this.setState({
      error,
      errorInfo,
    });
  }

  handleReload = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    });
    window.location.reload();
  };

  handleGoHome = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    });
    window.location.href = "/";
  };

  handleGoBack = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    });
    window.history.back();
  };

  render() {
    if (this.state.hasError) {
      const isDevelopment = ENV.MODE === "development";

      return (
        <ErrorContainer>
          <ErrorCard>
            <ErrorIcon>⚠️</ErrorIcon>
            <ErrorTitle>Oops! Something went wrong</ErrorTitle>
            <ErrorMessage>
              We encountered an unexpected error while loading this page. Don't
              worry, you can try reloading or go back to continue.
            </ErrorMessage>

            <ButtonGroup>
              <Button className="primary" onClick={this.handleReload}>
                🔄 Reload Page
              </Button>
              <Button className="secondary" onClick={this.handleGoBack}>
                ← Go Back
              </Button>
              <Button className="secondary" onClick={this.handleGoHome}>
                🏠 Go Home
              </Button>
            </ButtonGroup>

            {(isDevelopment || ENV.SHOW_ERROR_DETAILS === "true") && (
              <ErrorDetails>
                <summary>
                  Error Details (Error ID: {this.state.errorId})
                </summary>
                <pre>
                  <strong>Error:</strong> {this.state.error?.toString()}
                  {"\n\n"}
                  <strong>Component Stack:</strong>
                  {this.state.errorInfo?.componentStack}
                </pre>
              </ErrorDetails>
            )}
          </ErrorCard>
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}

/**
 * RouteErrorFallback Component
 *
 * A functional component alternative for simpler use cases.
 * Can be used with react-error-boundary library.
 */
export const RouteErrorFallback = ({
  error,
  resetErrorBoundary,
}: RouteErrorFallbackProps) => {
  const navigate = useNavigate();
  const isDevelopment = ENV.MODE === "development";

  const handleGoHome = () => {
    resetErrorBoundary();
    navigate("/");
  };

  const handleGoBack = () => {
    resetErrorBoundary();
    navigate(-1);
  };

  return (
    <ErrorContainer>
      <ErrorCard>
        <ErrorIcon>⚠️</ErrorIcon>
        <ErrorTitle>Oops! Something went wrong</ErrorTitle>
        <ErrorMessage>
          We encountered an unexpected error while loading this page. Don't
          worry, you can try reloading or go back to continue.
        </ErrorMessage>

        <ButtonGroup>
          <Button className="primary" onClick={resetErrorBoundary}>
            🔄 Try Again
          </Button>
          <Button className="secondary" onClick={handleGoBack}>
            ← Go Back
          </Button>
          <Button className="secondary" onClick={handleGoHome}>
            🏠 Go Home
          </Button>
        </ButtonGroup>

        {(isDevelopment || ENV.SHOW_ERROR_DETAILS === "true") && (
          <ErrorDetails>
            <summary>Error Details</summary>
            <pre>
              <strong>Error:</strong> {error?.toString()}
              {"\n\n"}
              <strong>Stack:</strong> {error?.stack}
            </pre>
          </ErrorDetails>
        )}
      </ErrorCard>
    </ErrorContainer>
  );
};

export default RouteErrorBoundary;
